import React, { useState, useEffect, useRef } from 'react';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUser } from '@fortawesome/free-solid-svg-icons';
import UserAvatar from './UserAvatar';
import { searchUsers } from '../../services/messagingService';
import './UserSearchInput.css';

function UserSearchInput({ onUserSelect, excludeUserIds = [], disabled = false }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    // Handle click outside to close results
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Debounce search
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, excludeUserIds]);

  const performSearch = async (query) => {
    try {
      setLoading(true);
      const results = await searchUsers(query);
      
      // Filter out excluded users
      const filteredResults = results.filter(
        user => !excludeUserIds.includes(user.userId)
      );
      
      setSearchResults(filteredResults);
      setShowResults(true);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (user) => {
    onUserSelect(user);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  return (
    <div className="user-search-input" ref={searchRef}>
      <div className="search-input-wrapper">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <Form.Control
          type="text"
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          disabled={disabled}
          className="search-input"
        />
        {loading && (
          <Spinner
            animation="border"
            size="sm"
            className="search-spinner"
          />
        )}
      </div>

      {showResults && searchResults.length > 0 && (
        <div className="search-results-dropdown">
          <ListGroup>
            {searchResults.map((user) => (
              <ListGroup.Item
                key={user.userId}
                action
                onClick={() => handleUserClick(user)}
                className="user-search-result"
              >
                <div className="d-flex align-items-center">
                  <UserAvatar
                    photoUrl={user.photoUrl}
                    displayName={user.displayName}
                    size="sm"
                    className="me-3"
                  />
                  <div className="user-info">
                    <div className="user-name">{user.displayName}</div>
                  </div>
                  {user.role && (
                    <div className="user-role ms-auto">
                      <span className="badge bg-secondary">{user.role}</span>
                    </div>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}

      {showResults && searchQuery.trim().length >= 2 && !loading && searchResults.length === 0 && (
        <div className="search-results-dropdown">
          <div className="no-results text-center text-muted py-3">
            <FontAwesomeIcon icon={faUser} size="2x" className="mb-2 opacity-25" />
            <p className="mb-0">No users found</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserSearchInput;
